import React, { useState } from 'react';
import { Box, Typography, Stack, Button, CircularProgress, Alert } from '@mui/material';
import { toast } from 'react-toastify';
import { descargarPactoDocumento, descargarPactoBadaroDocumento } from '../../services/pactoService';

const HABERES_SIN_PORCENTAJE = [1, 2, 3];
const HABERES_CON_PORCENTAJE = [2, 3];
const NOMBRE_ARCHIVO_BADARO = 'BADAROS + 2 Diferencias + 30%.docx';

function descargarBlob(data, nombreArchivo) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

/**
 * Sección para descargar el documento Word de PACTO (compromiso de pago de honorarios)
 * completado automáticamente con los datos del cliente seleccionado en el formulario
 * de iniciar trámite. Los datos del abogado y su matrícula quedan en blanco para
 * completarse a mano tras imprimir.
 *
 * `mostrarBadaro` habilita el pacto específico del reclamo BADARO (2 diferencias + 30%),
 * usado únicamente en Iniciar Trámite de Reajuste.
 *
 * `onPactoGenerado({ variante, numeroHaberes })` avisa qué pacto se emitió. Acá el trámite
 * TODAVÍA NO EXISTE -- esta sección vive dentro del formulario de alta, antes del submit --
 * así que el adjunto no se puede hacer desde acá: el formulario junta las elecciones con
 * `usePactosPendientes` y las manda recién cuando tiene el idTramite. Sin la prop (la página
 * "Generar Documentos" no la pasa) el componente se comporta igual que siempre: solo descarga.
 */
export default function SeccionGenerarPacto({ cliente, mostrarBadaro = false, onPactoGenerado }) {
    const [tipoPacto, setTipoPacto] = useState(null); // 'SIN_PORCENTAJE' | 'CON_PORCENTAJE' | 'BADARO'
    const [descargando, setDescargando] = useState(null);
    const [error, setError] = useState(null);

    if (!cliente?.cuil) {
        return null;
    }

    const MENSAJE_ERROR = 'No se pudo generar el documento PACTO. Verifique que el cliente tenga domicilio cargado.';

    const handleSeleccionarTipo = (tipo) => {
        setError(null);
        setTipoPacto((actual) => (actual === tipo ? null : tipo));
    };

    const handleDescargar = async (numeroHaberes, conPorcentaje) => {
        const clave = `${numeroHaberes}-${conPorcentaje}`;
        setDescargando(clave);
        setError(null);
        try {
            const response = await descargarPactoDocumento({
                cuil: cliente.cuil,
                numeroHaberes,
                conPorcentaje,
            });

            const nombreArchivo = `PACTO X ${numeroHaberes} HABERES${conPorcentaje ? ' + 30%' : ''}.docx`;
            descargarBlob(response.data, nombreArchivo);

            toast.success(`Descargando ${nombreArchivo}`);
            onPactoGenerado?.({
                variante: conPorcentaje ? 'CON_PORCENTAJE' : 'SIN_PORCENTAJE',
                numeroHaberes,
            });
        } catch (err) {
            console.error('❌ Error generando documento PACTO:', err);
            setError(MENSAJE_ERROR);
        } finally {
            setDescargando(null);
        }
    };

    const handleDescargarBadaro = async () => {
        setDescargando('BADARO');
        setError(null);
        try {
            const response = await descargarPactoBadaroDocumento({ cuil: cliente.cuil });
            descargarBlob(response.data, NOMBRE_ARCHIVO_BADARO);
            toast.success(`Descargando ${NOMBRE_ARCHIVO_BADARO}`);
            onPactoGenerado?.({ variante: 'BADARO', numeroHaberes: null });
        } catch (err) {
            console.error('❌ Error generando documento PACTO BADARO:', err);
            setError(MENSAJE_ERROR);
        } finally {
            setDescargando(null);
        }
    };

    const opcionesHaberes = tipoPacto === 'CON_PORCENTAJE' ? HABERES_CON_PORCENTAJE : HABERES_SIN_PORCENTAJE;

    return (
        <Box>
            <div style={{
                background: "var(--gradient-primary-light)",
                padding: "1rem 1.5rem",
                borderRadius: "12px",
                border: "2px solid #bfdbfe"
            }}>
                <Typography variant="h6" fontWeight={600} color="#1e40af">
                    <i className="bi bi-file-earmark-word me-2"></i>Generar Documentos - PACTO
                </Typography>
            </div>

            <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <Button
                    variant={tipoPacto === 'SIN_PORCENTAJE' ? 'contained' : 'outlined'}
                    onClick={() => handleSeleccionarTipo('SIN_PORCENTAJE')}
                    sx={{ borderRadius: '10px', textTransform: 'none' }}
                >
                    PACTO
                </Button>
                <Button
                    variant={tipoPacto === 'CON_PORCENTAJE' ? 'contained' : 'outlined'}
                    onClick={() => handleSeleccionarTipo('CON_PORCENTAJE')}
                    sx={{ borderRadius: '10px', textTransform: 'none' }}
                >
                    PACTO + 30%
                </Button>
                {mostrarBadaro && (
                    <Button
                        variant={tipoPacto === 'BADARO' ? 'contained' : 'outlined'}
                        onClick={() => handleSeleccionarTipo('BADARO')}
                        sx={{ borderRadius: '10px', textTransform: 'none' }}
                    >
                        BADARO
                    </Button>
                )}
            </Stack>

            {tipoPacto === 'BADARO' && (
                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        color="primary"
                        disabled={descargando === 'BADARO'}
                        startIcon={descargando === 'BADARO'
                            ? <CircularProgress size={16} />
                            : <i className="bi bi-download"></i>}
                        onClick={handleDescargarBadaro}
                        sx={{ borderRadius: '10px', textTransform: 'none' }}
                    >
                        {NOMBRE_ARCHIVO_BADARO.replace('.docx', '')}
                    </Button>
                </Stack>
            )}

            {(tipoPacto === 'SIN_PORCENTAJE' || tipoPacto === 'CON_PORCENTAJE') && (
                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    {opcionesHaberes.map((numeroHaberes) => {
                        const conPorcentaje = tipoPacto === 'CON_PORCENTAJE';
                        const clave = `${numeroHaberes}-${conPorcentaje}`;
                        const etiqueta = `PACTO X ${numeroHaberes} HABERES${conPorcentaje ? ' + 30%' : ''}`;
                        return (
                            <Button
                                key={clave}
                                variant="outlined"
                                color="primary"
                                disabled={descargando === clave}
                                startIcon={descargando === clave
                                    ? <CircularProgress size={16} />
                                    : <i className="bi bi-download"></i>}
                                onClick={() => handleDescargar(numeroHaberes, conPorcentaje)}
                                sx={{ borderRadius: '10px', textTransform: 'none' }}
                            >
                                {etiqueta}
                            </Button>
                        );
                    })}
                </Stack>
            )}

            {error && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>
                    {error}
                </Alert>
            )}
        </Box>
    );
}
